// apps/admin/src/features/users/AdminUserPermissionsSection.tsx
//
// Phase 8.2 Wave 7 Task 50 — Permissions section of the Admin User Detail.
//
// Read-only colon-notation tree shaped from the BE-resolved
// `effective_permissions[]` array via parsePermissionTree (task-03 helper).
// Each top-level group is the colon-prefix domain (invoice, refund_task,
// credit_memo, admin_user, etc.) — NOT a raw flat list.
//
// vg-binding: PLAN:task-50
// vg-binding: P8.D-101  — permissions tree is categorized by domain prefix
//                          (rendered groups), not a flat raw key array
// vg-binding: P8.D-495  — colon-notation RBAC namespace

import { useMemo } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  parsePermissionTree,
  type PermissionTreeNode,
} from '../../lib/permissions.js';

type Translate = (key: string, vars?: Record<string, unknown>) => string;

interface AdminUserPermissionsSectionProps {
  effectivePermissions: ReadonlyArray<string>;
  customPermissions: ReadonlyArray<string>;
}

/**
 * Top-level prefixes that have a localized label under
 * `permission.domainLabels.*`. Unknown prefixes render verbatim
 * (snake_case → Title Case) so the tree never crashes on a new domain.
 */
const KNOWN_DOMAINS: ReadonlySet<string> = new Set([
  'invoice',
  'credit_memo',
  'refund_task',
  'aging_report',
  'legal_entity',
  'pricing',
  'products',
  'product',
  'support',
  'user',
  'admin_user',
  'system',
  'dashboard',
  'orders',
  'vendors',
  'merchants',
  'stores',
  'finance',
  'impersonate',
]);

function humanizeSegment(seg: string, t: Translate): string {
  if (KNOWN_DOMAINS.has(seg)) return t(`permission.domainLabels.${seg}`);
  return seg
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

interface TreeRowProps {
  node: PermissionTreeNode;
  depth: number;
  customSet: ReadonlySet<string>;
}

function TreeRow({ node, depth, customSet }: TreeRowProps): JSX.Element {
  const { t } = useTranslation('users');
  const isLeaf = node.children.length === 0;
  const isCustomGrant = node.fullKey != null && customSet.has(node.fullKey);

  return (
    <li className="text-sm">
      <div
        className="flex items-center gap-2 py-1"
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        <span
          className={
            isLeaf
              ? 'inline-block h-2 w-2 rounded-full bg-emerald-500'
              : 'inline-block h-2 w-2 rounded-full bg-pw-border'
          }
          aria-hidden
        />
        <span className={depth === 0 ? 'font-semibold text-pw-text' : 'text-pw-text'}>
          {depth === 0 ? humanizeSegment(node.segment, t) : node.segment}
        </span>
        {node.fullKey && (
          <code className="rounded bg-pw-surface px-1.5 py-0.5 text-xs text-pw-text-muted">
            {node.fullKey}
          </code>
        )}
        {isCustomGrant && (
          <span className="rounded bg-amber-100 dark:bg-amber-950/40 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300">
            {t('permission.customBadge')}
          </span>
        )}
      </div>
      {node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <TreeRow
              key={`${node.segment}/${child.segment}`}
              node={child}
              depth={depth + 1}
              customSet={customSet}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function AdminUserPermissionsSection({
  effectivePermissions,
  customPermissions,
}: AdminUserPermissionsSectionProps): JSX.Element {
  const { t } = useTranslation('users');
  const tree = useMemo(
    () => parsePermissionTree(effectivePermissions),
    [effectivePermissions],
  );
  const customSet = useMemo(
    () => new Set(customPermissions),
    [customPermissions],
  );

  if (tree.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-pw-border p-6 text-center text-sm text-pw-text-muted">
        {t('detail.permissions.emptyNoPerms')}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-test-id="admin-user-permissions-tree">
      <p className="text-sm text-pw-text-muted">
        {t('detail.permissions.description')}
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {tree.map((root) => (
          <div
            key={root.segment}
            className="rounded-lg border border-pw-border bg-pw-bg p-3"
          >
            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-pw-text">
              {humanizeSegment(root.segment, t)}
            </h4>
            <ul className="space-y-0.5">
              {root.children.length === 0 ? (
                <TreeRow node={root} depth={0} customSet={customSet} />
              ) : (
                root.children.map((child) => (
                  <TreeRow
                    key={child.segment}
                    node={child}
                    depth={0}
                    customSet={customSet}
                  />
                ))
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
